import { CommonProp, Heading, TabItem } from '@acin/ui-core';
import React from 'react';
import { useMapControls } from '../../MapControlsProvider';
import { useRegulation } from '../../RegulationProvider';
import { IInternalRegulationObligationsQuery } from '../graphql/queries/internalRegulationObligations.generated';
import { ControlDetails } from './ControlDetails';
import { ControlMappingsList } from './ControlMappingsList';
import * as SC from './MapControls.styled';
import { MapControlsFooter } from './MapControlsFooter';
import { ObligationsList } from './ObligationsList';
import { ProcessesList } from './ProcessesList';
import { RisksList } from './RisksList';
import { RulesList } from './RulesList';

export interface TabData extends TabItem {
  title: string;
  id: string;
  content: () => React.ReactElement;
}

export interface MapControlsProps extends CommonProp {
  className?: string;
}

export const MapControls: React.FC<MapControlsProps> = ({ className }) => {
  const { activeObligation, onUpdateActiveObligation, reviewedObligationIds } = useMapControls();
  const { activeDocument, onUpdateShowDocumentModal } = useRegulation();
  const selectedObligationId = activeObligation?.id;

  // Auto-show document selection modal when no active document
  React.useEffect(() => {
    if (!activeDocument) {
      onUpdateShowDocumentModal(true);
    }
  }, [activeDocument, onUpdateShowDocumentModal]);

  const tabData: TabData[] = [
    {
      title: 'Rules',
      id: 'rules-tab',
      content: () => <RulesList />,
    },
    {
      title: 'Processes',
      id: 'processes-tab',
      content: () => <ProcessesList selectedObligationId={selectedObligationId} />,
    },
    {
      title: 'Risks',
      id: 'risks-tab',
      content: () => <RisksList selectedObligationId={selectedObligationId} />,
    },
  ];

  const onObligationsQueryCompleted: (data: IInternalRegulationObligationsQuery) => void = (data) => {
    const activeObligationInData = data?.internalRegulationObligationsByDocument?.edges.find(
      ({ node }) => node.id === activeObligation?.id,
    );

    if (
      !activeObligationInData ||
      (!activeObligation && data?.internalRegulationObligationsByDocument?.edges.length > 0)
    ) {
      onUpdateActiveObligation(data.internalRegulationObligationsByDocument.edges[0].node);
    }
  };

  return (
    <SC.MapControls className={className}>
      <SC.Columns>
        <SC.LeftColumn>
          <SC.LeftSubColumn>
            <SC.ColumnHeader>
              <Heading size={2}>Obligations</Heading>
            </SC.ColumnHeader>
            <SC.LeftSubColumnContent>
              <ObligationsList
                activeObligation={activeObligation}
                onUpdateActiveObligation={onUpdateActiveObligation}
                selectionMode="radio"
                onCompleted={onObligationsQueryCompleted}
                reviewedObligationIds={reviewedObligationIds}
              />
            </SC.LeftSubColumnContent>
          </SC.LeftSubColumn>
          <SC.LeftSubColumn>
            <SC.ColumnHeader>
              <Heading size={2}>{activeObligation?.title || 'Rules'}</Heading>
            </SC.ColumnHeader>
            <SC.LeftSubColumnContent>
              <SC.StyledTabs variant="sub" data={tabData} />
            </SC.LeftSubColumnContent>
          </SC.LeftSubColumn>
        </SC.LeftColumn>
        <SC.RightColumns>
          <ControlMappingsList />
          <SC.RightSubColumn>
            <SC.ColumnHeader>
              <Heading size={2}>Control Details</Heading>
            </SC.ColumnHeader>
            <SC.ContentSection>
              <ControlDetails />
            </SC.ContentSection>
          </SC.RightSubColumn>
        </SC.RightColumns>
      </SC.Columns>
      <MapControlsFooter />
    </SC.MapControls>
  );
};
